import { motion } from 'framer-motion';
import { Calendar, Clock, Eye, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';
import AuthorAvatar from '../components/AuthorAvatar';
import Breadcrumb from '../components/Breadcrumb';
import PageHero from '../components/PageHero';
import { getPageHeroImageUrl } from '../config/cloudflareAssets';
import { blogApi, BlogPost, BlogTag, BlogCategory } from '../services/api';

// Using real API calls now

export default function BlogIndexPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [tags, setTags] = useState<BlogTag[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [totalPages, setTotalPages] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const selectedTag = searchParams.get('tag') || '';
  const selectedCategory = searchParams.get('category') || '';
  const activeSearch = searchParams.get('search') || '';
  const requestedPage = Number.parseInt(searchParams.get('page') || '1', 10);
  const currentPage = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const hasActiveFilters = Boolean(selectedTag || selectedCategory || activeSearch);

  useEffect(() => {
    let isCurrentRequest = true;
    setSearchTerm(searchParams.get('search') || '');

    const loadBlogData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [postsResponse, tagsResponse, categoriesResponse] = await Promise.all([
          blogApi.public.getPosts({
            search: searchParams.get('search') || undefined,
            tag: searchParams.get('tag') || undefined,
            category: searchParams.get('category') || undefined,
            page: currentPage,
            limit: 9,
          }),
          blogApi.tags.getTags(),
          blogApi.categories.getCategories(),
        ]);
        if (!isCurrentRequest) return;
        if (!postsResponse.success || !postsResponse.data) {
          throw new Error(postsResponse.error || 'Failed to load blog posts');
        }
        if (!tagsResponse.success || !tagsResponse.data) {
          throw new Error(tagsResponse.error || 'Failed to load article topics');
        }
        if (!categoriesResponse.success || !categoriesResponse.data) {
          throw new Error(categoriesResponse.error || 'Failed to load article categories');
        }

        const lastPage = Math.max(1, postsResponse.data.pagination.totalPages);
        setPosts(postsResponse.data.posts);
        setTotalPages(lastPage);
        setTags(tagsResponse.data);
        setCategories(categoriesResponse.data);
        if (currentPage > lastPage) {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set('page', String(lastPage));
          setSearchParams(nextParams, { replace: true });
        }
      } catch (err) {
        if (!isCurrentRequest) return;
        console.error('Error loading blog data:', err);
        setPosts([]);
        setTotalPages(1);
        setError(err instanceof Error ? err.message : 'Failed to load blog posts');
      } finally {
        if (isCurrentRequest) setIsLoading(false);
      }
    };
    void loadBlogData();
    return () => {
      isCurrentRequest = false;
    };
  }, [searchParams, currentPage, setSearchParams, refreshKey]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const nextParams = new URLSearchParams(searchParams);
    if (searchTerm.trim()) nextParams.set('search', searchTerm.trim());
    else nextParams.delete('search');
    nextParams.set('page', '1');
    setSearchParams(nextParams);
  };

  const handleTagFilter = (tagSlug: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (tagSlug === selectedTag) nextParams.delete('tag');
    else nextParams.set('tag', tagSlug);
    nextParams.set('page', '1');
    setSearchParams(nextParams);
  };

  const handleCategoryFilter = (categorySlug: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (categorySlug === selectedCategory) nextParams.delete('category');
    else nextParams.set('category', categorySlug);
    nextParams.set('page', '1');
    setSearchParams(nextParams);
  };

  const clearFilters = () => {
    const nextParams = new URLSearchParams(searchParams);
    for (const key of ['search', 'tag', 'category']) nextParams.delete(key);
    nextParams.set('page', '1');
    setSearchTerm('');
    setSearchParams(nextParams);
  };

  const handlePageChange = (page: number) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('page', String(Math.min(totalPages, Math.max(1, page))));
    setSearchParams(nextParams);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <MarketingLayout>
      <div className="public-breadcrumb">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb items={[{ label: 'Blog' }]} />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        title="Fresh thinking for your next strategic move."
        subtitle="Ideas, frameworks and practical guidance for the decisions ahead—from shaping your direction to putting a plan into action."
      >
        <form onSubmit={handleSearch} className="max-w-xl mx-auto">
          <div className="flex rounded-xl bg-white border border-blue-100 shadow-sm overflow-hidden">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Find ideas, frameworks or advice..."
              aria-label="Search articles"
              className="min-w-0 flex-1 px-5 py-4 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            />
            <button
              type="submit"
              aria-label="Search articles"
              className="bg-blue-600 hover:bg-blue-700 px-6 py-3 text-white transition-colors"
            >
              <Search className="h-5 w-5" />
            </button>
          </div>
        </form>
      </PageHero>

      {/* Main Content */}
      <section className="public-section">
        <div className="container mx-auto px-4">
          <div className="lg:grid lg:grid-cols-4 lg:gap-8">
            {/* Sidebar */}
            <aside className="lg:col-span-1 mb-8 lg:mb-0">
              <div className="public-card public-card--blue p-6 lg:sticky lg:top-28">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Explore topics</h3>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={clearFilters}
                    aria-pressed={!hasActiveFilters}
                    className={`w-full rounded-lg border p-3 text-left font-medium ${!hasActiveFilters ? 'border-blue-200 bg-white text-blue-700' : 'border-transparent hover:bg-white'}`}
                  >
                    All articles
                  </button>
                  {categories.map((category) => (
                    <button
                      type="button"
                      key={category.slug}
                      onClick={() => handleCategoryFilter(category.slug)}
                      aria-pressed={selectedCategory === category.slug}
                      className={`w-full flex items-center justify-between gap-3 p-3 rounded-lg text-left transition-colors border ${selectedCategory === category.slug ? 'border-blue-200 bg-white text-blue-700' : 'border-transparent hover:bg-white'}`}
                    >
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: category.color }}
                        />
                        <span className="font-medium text-gray-900">{category.name}</span>
                      </div>
                      <span className="text-sm text-gray-500">{category.postCount || 0}</span>
                    </button>
                  ))}
                </div>
                {tags.length > 0 && (
                  <div className="mt-6 border-t border-blue-100 pt-5">
                    <h4 className="font-semibold mb-3">Browse by tag</h4>
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <button
                          key={tag.slug}
                          type="button"
                          onClick={() => handleTagFilter(tag.slug)}
                          aria-pressed={selectedTag === tag.slug}
                          className={`rounded-full border px-3 py-1 text-sm ${selectedTag === tag.slug ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-blue-100 text-blue-700 hover:border-blue-300'}`}
                        >
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>

            {/* Blog Posts Grid */}
            <div className="lg:col-span-3 min-w-0">
              {hasActiveFilters && (
                <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                  <p className="text-sm text-gray-600">
                    {[
                      activeSearch && `Search: ${activeSearch}`,
                      selectedCategory &&
                        `Category: ${categories.find((category) => category.slug === selectedCategory)?.name || selectedCategory}`,
                      selectedTag &&
                        `Tag: ${tags.find((tag) => tag.slug === selectedTag)?.name || selectedTag}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-sm font-medium text-blue-600 hover:underline"
                  >
                    Clear filters
                  </button>
                </div>
              )}
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : error ? (
                <div className="text-center py-12" role="alert">
                  <p className="text-red-600">{error}</p>
                  <button
                    type="button"
                    onClick={() => setRefreshKey((value) => value + 1)}
                    className="public-button public-button--secondary mt-5"
                  >
                    Try again
                  </button>
                </div>
              ) : posts.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">No blog posts found.</p>
                </div>
              ) : (
                <>
                  <div className="grid gap-6 md:grid-cols-2">
                    {posts.map((post, index) => (
                      <motion.article
                        key={post.id}
                        initial={false}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className={`public-card public-card--white overflow-hidden ${index === 0 ? 'md:col-span-2' : ''}`}
                      >
                        <Link to={`/blog/${post.slug}`}>
                          <div className="overflow-hidden">
                            <img
                              src={post.coverImage}
                              alt={post.title}
                              className={`w-full object-cover ${index === 0 ? 'h-56 md:h-80' : 'h-56'}`}
                              loading="lazy"
                            />
                          </div>
                        </Link>

                        <div className="p-6 md:p-8">
                          <div className="flex items-center space-x-2 mb-3">
                            {post.tags.slice(0, 2).map((tag) => (
                              <span
                                key={tag.slug}
                                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium"
                                style={{
                                  backgroundColor: tag.color + '20',
                                  color: tag.color,
                                }}
                              >
                                {tag.name}
                              </span>
                            ))}
                          </div>

                          <Link to={`/blog/${post.slug}`}>
                            <h2
                              className={`font-bold text-gray-900 mb-3 hover:text-blue-600 transition-colors line-clamp-3 ${index === 0 ? 'text-2xl md:text-3xl' : 'text-2xl'}`}
                            >
                              {post.title}
                            </h2>
                          </Link>

                          <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>

                          <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center space-x-3">
                              <AuthorAvatar
                                photo={post.author.photo}
                                name={post.author.name}
                                className="w-8 h-8 rounded-full"
                              />
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {post.author.name}
                                </p>
                                <div className="flex items-center space-x-2 text-xs text-gray-500">
                                  <Calendar className="h-3 w-3" />
                                  <span>{formatDate(post.publishedAt)}</span>
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center space-x-3 text-xs text-gray-500">
                              <div className="flex items-center space-x-1">
                                <Clock className="h-3 w-3" />
                                <span>{post.readingTimeMinutes}m</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Eye className="h-3 w-3" />
                                <span>{post.viewCount}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.article>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex flex-wrap items-center justify-center gap-2 mt-10">
                      <button
                        aria-label="Previous page"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>

                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-2 rounded-lg font-medium ${
                            currentPage === page
                              ? 'bg-blue-600 text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}

                      <button
                        aria-label="Next page"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
      <section className="public-cta">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Turn a good idea into a plan you can act on.
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-7">
            Connect your direction, business analysis, priorities and people in one workspace.
          </p>
          <Link to="/sign-up" className="public-button public-button--primary">
            Start my strategic plan
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
